import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import urls from "utils/constants/urls";
import { Creators as BirthdaysActions } from "store/ducks/birthdays";
import Portal from "templates/Portal";
import Flex from "components/core/Grid/Flex";
import Avatar from "components/core/Avatar/Avatar";
import { PageTitle } from "components/core/Typography/Typography";
import { ReactComponent as Birthday } from "assets/birthdays.svg";
import { smartGet, smartFormatterDate } from "helpers/sharedHelpers";
import { useAnalytics } from "utils/hooks";
import BirthdaysTodaySkeleton from "./BirthdaysTodaySkeleton";

import {
  BirthdaysCount,
  BirthdayDate,
  BirthdayToday,
  MembersList,
  MemberItem,
  MemberName,
  BirthdayTodayBox,
} from "../BirthdaysStyle";

useAnalytics("screen_birthday_day");

const BirthdaysToday = () => {
  const intl = useIntl();
  const memberListRef = useRef();
  const dispatch = useDispatch();
  const currentDay = smartGet(moment(), "date");
  const currentMonth = smartGet(moment(), "month");
  const { birthdays, isLoading } = useSelector((state) => state.birthdays);
  const dispatchGetBirthdays = useCallback(
    () => dispatch(BirthdaysActions.getBirthdays()),
    [dispatch],
  );
  useEffect(() => {
    dispatchGetBirthdays();
  }, [dispatchGetBirthdays]);

  const renderName = (name) => {
    const first = name[0];
    const last = name.slice(-1)[0];

    return first === last ? first : `${first} ${last}`;
  };

  const birthdaysToday = () => {
    const length = birthdays[currentMonth]?.members?.filter(
      (member) => currentDay === smartGet(member?.datebirthday, "date"),
    ).length;
    return (
      length > 0 && (
        <>
          <BirthdayDate>
            <BirthdaysCount>
              <BirthdayToday>{`${smartFormatterDate(
                moment(),
                "LL",
              )}`}</BirthdayToday>
            </BirthdaysCount>
          </BirthdayDate>
          <Flex>
            <Birthday width={32} height={32} />
            <BirthdaysCount>
              <BirthdayToday>{`${intl.messages["birthdays.textPage.todayWeHave"]} `}</BirthdayToday>
              {length === 1
                ? `${length} ${intl.messages["birthdays.textPage.birthday"]}`
                : `${length} ${intl.messages["birthdays.textPage.birthdayPlural"]}`}
            </BirthdaysCount>
          </Flex>
        </>
      )
    );
  };

  const renderTitleMember = ({ sex, name }) => {
    let genres;
    switch (sex) {
      case "M":
        genres = intl.messages["general.textPage.genres.male"];
        break;
      case "F":
        genres = intl.messages["general.textPage.genres.feminine"];
        break;
      default:
        genres = intl.messages["general.textPage.genres.ungenerous"];
    }
    return `${intl.messages["general.textPage.clickOfAcessProfile"]} ${genres} ${name}`;
  };

  return (
    <Portal>
      <BirthdayTodayBox flexDirection="column">
        <PageTitle title={intl.messages["menu.birthdays"]}>
          {isLoading ? (
            <>
              <BirthdayDate>
                <Skeleton duration={0.8} width={180} height={28} />
              </BirthdayDate>
              <Skeleton duration={0.8} width={396} height={28} />
            </>
          ) : (
            birthdaysToday()
          )}
        </PageTitle>
        {isLoading ? (
          <BirthdaysTodaySkeleton />
        ) : (
          birthdays && (
            <Flex>
              <MembersList ref={memberListRef}>
                {birthdays[currentMonth]?.members
                  ?.filter(
                    (member) =>
                      currentDay === smartGet(member.datebirthday, "date"),
                  )
                  .map(({ id, name, image, sex }) => {
                    const arrayName = name.toLowerCase().split(" ");

                    return (
                      <MemberItem
                        title={renderTitleMember({ sex, name })}
                        to={`${urls.ROUTES.PROFILE}/${id}`}
                        key={id}
                      >
                        <Avatar url={image?.url} size="120px" />
                        <MemberName title={renderName(arrayName)}>
                          {renderName(arrayName)}
                        </MemberName>
                      </MemberItem>
                    );
                  })}
              </MembersList>
            </Flex>
          )
        )}
      </BirthdayTodayBox>
    </Portal>
  );
};

export default BirthdaysToday;
